import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import SaveButton from "../../components/admin/SaveButton";
import { useUploadStatus } from "../../context/UploadStatusContext";

const empty = {
    category: "", title: "", story: "", image_url: "", order: 0, is_active: true,
};

export default function AdminMoments() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const { isUploading } = useUploadStatus();

    const load = () => api.get("/admin/moments").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing("new"); setForm({ ...empty, order: items.length }); };
    const openEdit = (m) => { setEditing(m.id); setForm(m); };
    const close = () => { setEditing(null); setForm(empty); };

    const save = async (e) => {
        e.preventDefault();
        if (isUploading) return;
        try {
            if (editing === "new") await api.post("/moments", form);
            else await api.put(`/moments/${editing}`, form);
            toast.success("Saved"); close(); load();
        } catch { toast.error("Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this moment?")) return;
        try { await api.delete(`/moments/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="admin-moments">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display text-3xl">Special Moments</h2>
                    <p className="text-sm text-ink-soft mt-1">The 4 editorial photos on the homepage, under "The cakes behind the celebrations."</p>
                </div>
                <button onClick={openNew} className="btn-pink" data-testid="add-moment"><Plus size={14} /> New</button>
            </div>

            <div className="space-y-2">
                {items.map((m, i) => (
                    <div key={m.id} className="bg-white border border-[#EAE3DE] p-4 flex items-center gap-4" data-testid={`moment-row-${i}`}>
                        <img src={m.image_url} alt={m.title} className="w-16 h-16 object-cover" />
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-lg">{m.title}</p>
                            <p className="text-xs text-ink-soft">{m.is_active ? "Active" : "Hidden"} · {m.category} · order {m.order}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => openEdit(m)} data-testid={`edit-moment-${i}`}><Edit size={16} /></button>
                            <button onClick={() => remove(m.id)} data-testid={`delete-moment-${i}`}><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <p className="text-sm text-ink-soft">No moments yet.</p>}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-2xl">{editing === "new" ? "New" : "Edit"} Moment</h3>
                            <button onClick={close}><X size={20} /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                            <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Category (e.g. Wedding, Hens)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required />
                            <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={4} placeholder="Story" value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} required />
                            <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} label="Photo" testIdPrefix="moment-image" />
                            <div>
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Order</label>
                                <input type="number" className="w-full px-4 py-3 border border-[#EAE3DE]" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
                            </div>
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                <span className="text-sm">Active (shown on homepage)</span>
                            </label>
                            <SaveButton />
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
